/**
 * About — `/about` route.
 *
 * Visual language borrowed from the 8 PageHost marketing pages
 * (/learn, /network, /solutions/*): night hero with editorial ambient,
 * sticky anchor nav, alternating day/night sections, diamond-marker
 * eyebrows, italic-accented serif headlines, numbered "entry" cards,
 * and a vertical editorial timeline for the journey.
 *
 * Copy is preserved verbatim from the previous About page; no new
 * sections were added.
 */
import { Helmet } from "react-helmet-async";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AnchorNav } from "../components/AnchorNav";
import { ClosingCTA } from "../components/marketing/ClosingCTA";
import { Starfield } from "../components/marketing/Starfield";
import { Reveal } from "../components/anim";
import "../styles/about-design.css";

const anchorItems = [
  { label: "Mission", href: "#mission" },
  { label: "Governance", href: "#governance" },
  { label: "Journey", href: "#journey" },
];

const kpis = [
  { value: "$32B+", label: "Tokenised on ERC-3643" },
  { value: "20+", label: "Jurisdictions live" },
  { value: "8 yrs", label: "In production" },
];

const journey = [
  { year: "2017", event: "Origins of the T-REX protocol. First work on compliance-aware tokens." },
  { year: "2020", event: "ERC-3643 standard formalised and submitted to Ethereum Foundation." },
  { year: "2024", event: "Final EIP status awarded. $32B+ tokenised on the standard." },
  { year: "2025", event: "Apex Group partnership. $100B target on T-REX Ledger." },
  { year: "2026", event: "T-REX Network public launch. The Ledger goes live as the book of record." },
];

const governance = [
  { layer: "Company", title: "Digital Asset Operational Services ISAC Ltd.", description: "Operating company stewarding the T-REX Network." },
  { layer: "Steering Committee", title: "Institutional members", description: "Anchor institutions and standard contributors providing strategic direction." },
  { layer: "PoA Validators", title: "Vetted validator set", description: "Permissioned authority validators operating the canonical ledger." },
  { layer: "ERC-3643 Association", title: "Standard governance", description: "Non-profit body of experts curating the ERC-3643 specification and reference implementations for the wider community." },
];

export default function About() {
  return (
    <main className="about-trex-design min-h-screen bg-aubergine" data-testid="about-page">
      <Helmet>
        <title>About · T-REX Network</title>
        <meta name="description" content="Eight years in production. The team behind ERC-3643 and the T-REX Network. Mission, governance, journey." />
        <meta property="og:title" content="About · T-REX Network" />
        <meta property="og:description" content="The team behind ERC-3643 and the T-REX Network." />
      </Helmet>

      <Navbar />

      {/* ============================== HERO ============================== */}
      <section
        className="surface-night with-bloom relative overflow-hidden pt-32 pb-24 lg:pt-40 lg:pb-32"
        data-testid="about-hero"
      >
        <Starfield density="medium" topBias={0.5} seed={13} className="z-[1]" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{
            background:
              "radial-gradient(ellipse 60rem 42rem at 12% 30%, rgba(232,217,184,0.12) 0%, rgba(232,217,184,0) 60%), radial-gradient(ellipse 42rem 32rem at 88% 68%, rgba(201,163,74,0.08) 0%, rgba(201,163,74,0) 60%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <h1
                className="trex-h1 text-bone"
                data-testid="about-headline"
              >
                Eight years in <em className="accent-italic">production</em>.
              </h1>
              <p
                className="mt-7 max-w-xl text-lg lg:text-xl leading-relaxed text-bone/75"
                data-testid="about-subheadline"
              >
                The team behind ERC-3643 and the T-REX Network.
              </p>
            </div>

            {/* KPI strip — three editorial stat cards inline with the hero */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {kpis.map((k, i) => (
                  <Reveal key={k.label} delay={200 + i * 120}>
                    <div
                      className="rounded-md border border-champ-light/15 bg-aubergine-tier/60 px-3 py-4 sm:px-4 sm:py-5 backdrop-blur-[1px] h-full"
                      data-testid={`about-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <p className="font-serif text-xl sm:text-2xl lg:text-3xl text-bone leading-none">{k.value}</p>
                      <p className="mt-2 sm:mt-3 font-mono text-[10px] text-bone/55 uppercase tracking-[0.1em] sm:tracking-[0.12em] leading-snug">
                        {k.label}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnchorNav items={anchorItems} />

      {/* ============================ MISSION ============================ */}
      <section
        id="mission"
        className="surface-day py-24 lg:py-32"
        data-testid="about-mission"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl">
              <h2 className="trex-h2 text-aubergine leading-[1.05]">
                Make regulated assets move <em className="accent-italic">onchain</em>.
              </h2>
              <p className="mt-6 text-base lg:text-lg text-aubergine/75 leading-relaxed">
                We build the T-REX Network, the neutral network for regulated tokenised assets. It brings together open standards, a shared book of record, and an ecosystem of institutional partners and applications. Our work began in 2017 with the first compliance-aware token contracts; today, ERC-3643 is the leading open standard for permissioned digital assets.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========================== GOVERNANCE ========================== */}
      <section
        id="governance"
        className="surface-day py-24 lg:py-32"
        data-testid="about-governance"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <h2 className="trex-h2 text-bone leading-[1.05]">
                How the <em className="accent-italic">Network</em> is governed.
              </h2>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {governance.map((g, idx) => (
              <Reveal key={g.layer} delay={idx * 90} className="h-full">
                <div
                  className="about-entry about-entry-lg h-full"
                  data-testid={`governance-${g.layer.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-mono text-[11px] tracking-[0.18em] text-champagne">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 font-mono text-[10px] text-champagne/75 uppercase tracking-[0.15em]">
                    {g.layer}
                  </p>
                  <h3 className="mt-3 governance-title text-aubergine">
                    {g.title}
                  </h3>
                  <p className="mt-4 text-sm text-aubergine/70 leading-relaxed">
                    {g.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ JOURNEY ============================ */}
      <section
        id="journey"
        className="surface-day py-24 lg:py-32"
        data-testid="about-journey"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <h2 className="trex-h2 text-aubergine leading-[1.05]">
                Eight years of <em className="accent-italic">compounding</em>.
              </h2>
            </div>
          </Reveal>

          <ol className="about-tl max-w-3xl">
            {journey.map((j, idx) => (
              <Reveal
                key={j.year}
                delay={idx * 110}
                as="li"
                className="about-tl-step"
              >
                <span className="about-tl-marker" aria-hidden="true">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-champagne" data-testid={`journey-${j.year}`}>
                  {j.year}
                </p>
                <p className="mt-2 text-base lg:text-lg text-aubergine/80 leading-relaxed">
                  {j.event}
                </p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <ClosingCTA variant="contact" tone="night" />
      <Footer />
    </main>
  );
}
